import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import type { RoastData } from 'features/roast/utils';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

interface IGetRoastsQueryFnParams {
  supabase: SupabaseClient<Database>;
  userId: string;
}

const getRoastsQueryFn = async ({
  supabase,
  userId,
}: IGetRoastsQueryFnParams): Promise<RoastData[]> => {
  const { data, error } = await supabase
    .from('roast')
    .select(
      'id, slug, url, user_id, screenshot_url, status, reveal_date, site_name, site_description, site_logo_url, detected_tech, category, pricing_model, tags, socials, contact_email, scores, roast_text, summary, upvote_count, comment_count, submitted_at, updated_at, is_promoted'
    )
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;

  return (data ?? []) as unknown as RoastData[];
};

interface IGetRoastsQueryParams {
  userId?: string | null;
}

interface IGetRoastsQueryResponse {
  data: RoastData[] | null;
  isPending: boolean;
}

export const useGetRoastsQuery = ({
  userId,
}: IGetRoastsQueryParams): IGetRoastsQueryResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetRoasts, userId],
    queryFn: () =>
      getRoastsQueryFn({
        supabase,
        userId: userId!,
      }),
    enabled: Boolean(userId),
  });

  return {
    data: data ?? null,
    isPending,
  };
};
